import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { CheckOutlined, Delete, Edit, ExpandMore } from "@mui/icons-material";
import React, { useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { MarkSchemeStepWrapper } from "./MarkSchemeStepWrapper";
import { getQuestionTypeMeta } from "../utils";

const StyledAccordion = ({ expandedTitle, collapsedTitle, content }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  return (
    <Box sx={{ mb: 2 }}>
      <Accordion expanded={expanded} variant={"outlined"}>
        <AccordionSummary onClick={() => setExpanded(!expanded)}>
          <Stack direction="row" alignItems="center">
            <Typography
              variant="subtitle2"
              fontWeight="medium"
              sx={{ color: theme.palette.primary.main }}
            >
              {expanded ? expandedTitle : collapsedTitle}
            </Typography>
            <ExpandMore />
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Box mt={1} pl={2}>
            {content}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const SourceTextAccordion = ({ question }) => {
  return (
    <StyledAccordion
      collapsedTitle={"Show source text"}
      expandedTitle={"Hide source text"}
      content={<Typography variant="body2">{question.sourceText}</Typography>}
    />
  );
};

const MarkSchemeAccordion = ({ question }) => {
  return (
    <StyledAccordion
      collapsedTitle={"Show mark scheme"}
      expandedTitle={"Hide mark scheme"}
      content={
        <MarkSchemeStepWrapper
          question={question}
          markScheme={question.markScheme}
          hideTitle
          hideQuestion
          readOnly
        />
      }
    />
  );
};

export const QuestionAccordion = ({
  question,
  number,
  onEditClicked,
  onCheckBoxClick,
  onRemoveClicked = null,
  disableEdit = false,
  defaultExpanded = false,
  hideChips = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const typeMeta = getQuestionTypeMeta(question);
  const hasSchoolFramework = question?.schoolFeedbackFrameworks?.length > 0;
  return (
    <Card data-testid={"question-accordion-card"}>
      <CardContent>
        <Accordion expanded={expanded}>
          <AccordionSummary
            component="div"
            onClick={() => setExpanded(!expanded)}
          >
            <Box display="flex" flexDirection="column" width="100%" gap={0.5}>
              <Box display="flex" alignItems="center">
                {onCheckBoxClick && (
                  <Checkbox
                    checked={question.include}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      onCheckBoxClick?.(question.id, e.target.checked);
                    }}
                  />
                )}
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ ml: 1 }}
                >
                  Question {number}
                </Typography>
                {!hideChips && (
                  <Chip
                    sx={{ ml: 1 }}
                    size={"medium"}
                    label={typeMeta.label}
                    variant="outlined"
                    icon={typeMeta.icon}
                    color={"primary"}
                  />
                )}

                {hasSchoolFramework && !hideChips && (
                  <Chip
                    sx={{ ml: 1 }}
                    size={"medium"}
                    label={"School Framework"}
                    color={"primary"}
                    variant="outlined"
                    icon={<CheckOutlined />}
                  />
                )}
                {!question.isPublished && !disableEdit && (
                  <>
                    <Tooltip
                      title={
                        !question.isComplete
                          ? "This question needs finishing before it can be used in an assignment"
                          : "" +
                            "This question will no longer be editable if used in this assignment"
                      }
                    >
                      {/* I dont think it is possible to add incomplete drafts to assignment as it should instead open the editing dialog */}
                      <Chip
                        label={
                          question.isComplete ? "Draft" : "Unfinished Draft"
                        }
                        color={question.isComplete ? "default" : "warning"}
                        sx={{
                          ml: 2,
                          border: "none",
                        }}
                      />
                    </Tooltip>
                    <Button
                      endIcon={<Edit />}
                      variant={"text"}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditClicked(question);
                      }}
                      sx={{
                        ml: 2,
                      }}
                    >
                      Edit
                    </Button>
                  </>
                )}
                <Box flexGrow={1} />
                <Stack direction={"row"} spacing={1}>
                  {onRemoveClicked && (
                    <Delete
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveClicked(question);
                      }}
                    />
                  )}
                  <ExpandMore />
                </Stack>
              </Box>

              <Box ml={6}>
                <Typography
                  variant="subtitle1"
                  fontWeight="regular"
                  sx={{
                    textOverflow: expanded ? "clip" : "ellipsis",
                  }}
                >
                  {question.questionText}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* TODO: the mark scheme should only be on Long question types / mark scheme exists, for MCQ / Bool - need to display answers */}
            {question.sourceText && <SourceTextAccordion question={question} />}
            <MarkSchemeAccordion question={question} />
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};
